import clsx from "clsx";
import { type ReactNode } from "react";
import useLocalStorageState from "use-local-storage-state";
import i18n from "../util/i18n.ts";

const HideableSectionButton = ({
  show,
  setShow,
}: {
  show: boolean;
  setShow: (show: boolean) => void;
}) => (
  <button
    aria-expanded={show}
    className="btn btn-light-bordered ms-2"
    data-hideable-section-action={show ? "hide" : "show"}
    onClick={() => {
      setShow(!show);
    }}
  >
    {show
      ? i18n.t("settings.hide", { ns: "ui", defaultValue: "Hide" })
      : i18n.t("settings.show", { ns: "ui", defaultValue: "Show" })}
  </button>
);

export const getHideableSectionStorageKey = (
  pageName: string | undefined,
  title: string,
  titleExtraKey?: string | number,
) => {
  let key = pageName === undefined ? `show-${title}` : `show-${pageName}-${title}`;
  if (titleExtraKey !== undefined) {
    key += `-${titleExtraKey}`;
  }
  return key;
};

const useShowSection = (
  pageName: string | undefined,
  title: string,
  titleExtraKey?: string | number,
) => {
  const key = getHideableSectionStorageKey(pageName, title, titleExtraKey);

  const [show, setShow] = useLocalStorageState(key, {
    defaultValue: true,
  });

  const hideableSectionButton = <HideableSectionButton show={show} setShow={setShow} />;

  return [key, show, hideableSectionButton] as const;
};

const HideableSection = ({
  children,
  className,
  description,
  pageName,
  renderTitle,
  title,
  titleExtraKey,
}: {
  children: ReactNode;
  className?: string;
  description?: ReactNode;

  // Undefind pagename is for backwards compatibility with original usage on player page
  pageName?: string | undefined;
  title: string;
  titleExtraKey?: string | number;

  // Use this to override title for display, including the button. title is still needed for the localStorage key
  renderTitle?: (show: boolean, hideableSectionButton: ReactNode) => ReactNode;
}) => {
  const [key, show, hideableSectionButton] = useShowSection(pageName, title, titleExtraKey);

  // z-index of 1 ensures that it is still clickable when used with datatable-negative-margin-top
  return (
    <section
      data-hideable-section-key={key}
      data-hideable-section-state={show ? "shown" : "hidden"}
      data-hideable-section-storage-scope={
        pageName === undefined && titleExtraKey === undefined ? "global" : "scoped"
      }
      data-hideable-section-title={title}
    >
      <div
        className={clsx(
          "d-flex align-items-center flex-wrap gap-2 row-gap-0",
          className,
          show ? "mb-2" : "mb-3",
        )}
      >
        {renderTitle ? (
          renderTitle(show, hideableSectionButton)
        ) : (
          <div className="d-flex z-1" style={{ minWidth: 0 }}>
            <h2 className="mb-0 text-truncate">{title}</h2>
            {hideableSectionButton}
          </div>
        )}
        {show ? <div className="text-nowrap">{description}</div> : null}
      </div>
      {show ? children : null}
    </section>
  );
};

export default HideableSection;
